import type { ActionContext } from 'vuex'

import type {
  ActionClass,
  AuditFilters,
  AuditItem,
  AuditState,
  DateFilterSelectedValues,
  Dictionary,
  Filter,
  Pagination,
  RegularFilter,
} from '../../../models'
import type { ContextProvider } from '../../../providers'

import type { IAuditService } from '../../../services'

import { CONTAINER } from '../../../config'
import { TYPES } from '../../../config/inversify.types'
import { FilterIdEnum } from '../../../enums'

import { AUDIT_CONSTS } from '../audit.consts'

export class ReadAction implements ActionClass {
  static async execute(
    this: void,
    { commit, state }: ActionContext<AuditState, any>,
    {
      pagination,
      filters,
      strictMatching,
    }: {
      pagination: Pagination
      filters?: AuditFilters
      strictMatching?: boolean
    },
  ): Promise<Array<AuditItem>> {
    const context = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
    const auditService = CONTAINER.get<IAuditService>(TYPES.AuditService)
    const response = await auditService.readAudit({
      strictMatching,
      offset: (pagination.page - 1) * pagination.itemsPerPage,
      rows: pagination.itemsPerPage + 1,
      query: context.query,
      ...ReadAction.getAuditRequestFilters(context.filters),
      ...filters,
    })
    const data = pagination.page === 1 ? response : state.data.slice(0, -1).concat(response)
    commit(AUDIT_CONSTS.mutations.setData, data)
    return response
  }

  static getAuditRequestFilters(filters: Dictionary<Filter>): AuditFilters {
    return Object.values(filters).reduce((requestFilters: AuditFilters, filter: Filter) => {
      const requestFilterFn = {
        [FilterIdEnum.DATE]: () => {
          const [startDateInMillis, endDateInMillis] =
            filter.selectedValues as DateFilterSelectedValues
          return {
            startDate: startDateInMillis ? new Date(startDateInMillis) : undefined,
            endDate: endDateInMillis ? new Date(endDateInMillis) : undefined,
          }
        },
        [FilterIdEnum.AUDIT_OBJECT_TYPE]: () => {
          const auditObjectTypeFilter = filter as RegularFilter
          const hasNotSelectedValues = auditObjectTypeFilter.selectedValues.length === 0

          return {
            types: hasNotSelectedValues
              ? auditObjectTypeFilter.config.values.map(({ id }) => id)
              : auditObjectTypeFilter.selectedValues,
          }
        },
        [FilterIdEnum.AUDIT_ACTION]: () => ({ actions: filter.selectedValues }),
      }[filter.config.id]
      const requestFilterObj = !!requestFilterFn && requestFilterFn()
      if (requestFilterObj) {
        Object.assign(requestFilters, requestFilterObj)
      }
      return requestFilters
    }, {})
  }
}
